import { feature, featureCollection } from '@turf/turf'
import type { FeatureCollection } from 'geojson'
import { Fragment, useMemo } from 'react'
import { Source } from 'react-map-gl/mapbox'
import { useSites } from '../../data/assets.ts'
import { isAssetLocation, type Location } from '../../data/locations.tsx'
import { useMapTheme } from '../../theme/mapPalette.ts'
import { OrderedLayer } from '../layers/customLayers.tsx'

type LocationGeometryProps = {
	locations: Location[]
	selectedLocations?: Location[]
}

export const LocationGeometry = (props: LocationGeometryProps) => {
	const { data: sites } = useSites()
	const geometries = useMemo(() => {
		const selectedIds = new Set(props.selectedLocations?.map(({ id }) => id))
		return featureCollection(
			props.locations.flatMap((location) =>
				isAssetLocation(location) ? [feature(location.geometry, { isSelected: selectedIds.has(location.id) })] : [],
			),
		)
	}, [props.locations, props.selectedLocations])
	const coordinateLabels = useMemo(() => {
		if (!sites) return featureCollection([])
		const assetSiteVrns = props.locations.map((asset) => (isAssetLocation(asset) ? asset.siteVrn : null)).filter(Boolean)
		const childSites = sites.filter((site) => site.parentVrn && assetSiteVrns.includes(site.parentVrn))
		return featureCollection(childSites.map((site) => feature(site.geom, { name: site.name })))
	}, [sites, props.locations])
	return (
		<Fragment>
			<GeometrySourceLayers data={geometries} selectedProperty='isSelected' />
			<LabelLayer data={coordinateLabels} minZoom={8} />
		</Fragment>
	)
}

export const GeometrySourceLayers = ({ data, selectedProperty = 'isSelected' }: { data: FeatureCollection; selectedProperty?: string }) => {
	const mapTheme = useMapTheme()
	const id = 'asset-geometry'
	return (
		<Source id={`${id}-source`} type='geojson' data={data}>
			{/* POLYGONS & MULTI-POLYGONS */}
			<OrderedLayer
				id={`${id}-polygon`}
				type='fill'
				paint={{
					'fill-color': ['case', ['get', selectedProperty], mapTheme.mapGeometry.selectedFill, mapTheme.mapGeometry.defaultFill],
					'fill-outline-color': [
						'case',
						['get', selectedProperty],
						mapTheme.mapGeometry.selectedStroke,
						mapTheme.mapGeometry.defaultStroke,
					],
				}}
				filter={['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]}
			/>
			{/* LINESTRINGS & MULTI-LINESTRINGS */}
			<OrderedLayer
				id={`${id}-linestring`}
				type='line'
				paint={{
					'line-color': ['case', ['get', selectedProperty], mapTheme.mapGeometry.selectedStroke, mapTheme.mapGeometry.defaultStroke],
					'line-width': 1.5,
				}}
				filter={['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]]}
			/>
			{/* POINTS & MULTI-POINTS */}
			<OrderedLayer
				id={`${id}-circle`}
				type='circle'
				paint={{
					'circle-radius': 4,
					'circle-color': ['case', ['get', selectedProperty], mapTheme.mapGeometry.selectedFill, mapTheme.mapGeometry.defaultFill],
					'circle-stroke-color': [
						'case',
						['get', selectedProperty],
						mapTheme.mapGeometry.selectedStroke,
						mapTheme.mapGeometry.defaultStroke,
					],
					'circle-stroke-width': 1,
				}}
				filter={['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]]}
			/>
		</Source>
	)
}

export const LabelLayer = ({ data, minZoom }: { data: FeatureCollection; minZoom: number }) => {
	const mapTheme = useMapTheme()
	return (
		<Source id='coordinate-label-source' type='geojson' data={data}>
			<OrderedLayer
				id='asset-coordinate-labels'
				type='symbol'
				source='coordinate-label-source'
				layout={{
					'text-field': ['get', 'name'],
					'text-size': 12,
					'text-anchor': 'top',
					'text-offset': [0, 0.5],
				}}
				paint={{
					'text-color': mapTheme.mapGeometry.labelFill,
					'text-halo-color': mapTheme.mapGeometry.labelStroke,
					'text-halo-width': 1,
					'text-opacity': ['interpolate', ['linear'], ['zoom'], minZoom, 0, minZoom + 1, 1],
				}}
			/>
		</Source>
	)
}
